import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { ToolbarDivider } from "./ToolbarButton.jsx";
import { ColorPanel } from "./ColorPanel.jsx";
import { OverflowPanel } from "./OverflowPanel.jsx";
import { LinkDialog } from "./LinkDialog.jsx";
import {
  COLOR_PRESETS,
  HIGHLIGHT_PRESETS,
} from "./MarkdownEditor.constants.js";
import { seedTableColumnWidths } from "./MarkdownEditor.utils.js";

/**
 * RichTextToolbar - main toolbar component with all formatting options
 */
const RichTextToolbar = forwardRef(
  (
    {
      editor,
      hasTool,
      isOverflow,
      onUploadImage,
      handleImageFiles,
      onUploadVideo,
      handleVideoFiles,
      activeColor,
      activeHighlight,
      activeAlign,
      colorOpen,
      setColorOpen,
      colorPos,
      setColorPos,
      colorBtnRef,
      colorPanelRef,
      highlightOpen,
      setHighlightOpen,
      highlightPos,
      setHighlightPos,
      highlightBtnRef,
      highlightPanelRef,
      linkOpen,
      setLinkOpen,
      linkPos,
      setLinkPos,
      linkValue,
      setLinkValue,
      linkBtnRef,
      linkDialogRef,
      overflowOpen,
      setOverflowOpen,
      overflowPos,
      setOverflowPos,
      overflowBtnRef,
      overflowRef,
      isInTable,
      toolbarTabStop = false,
      onEscapeToProse,
    },
    ref,
  ) => {
    /** THE TOOLBAR STRIP ITSELF, for the roving tabindex below. */
    const toolbarRef = useRef(null);
    /**
     * Where the roving stop IS — the control a Tab (when `toolbarTabStop`) or an Alt+F10 lands
     * on. Remembered whether or not the strip is a tab stop, because Alt+F10 has to return the
     * author to the tool they were last using either way.
     */
    const activeControlRef = useRef(null);

    const setToolbarRef = (node) => {
      toolbarRef.current = node;
    };

    /**
     * THE TOOLBAR'S CONTROLS, IN DOM ORDER — the roving tabindex's ring.
     *
     * Read out of the DOM rather than kept as a list, because the strip's membership is not
     * knowable from here: every group is conditional on `hasTool` / `isOverflow`, the table
     * group appears and disappears with the caret's position (`isInTable`), and image/video
     * exist only when an uploader was passed. A hand-maintained index would have to be
     * recomputed for all of that and would silently drift the first time a tool was added.
     *
     * Scoped to `.rte-toolbar`, which is why the floating panels are untouched by any of this:
     * the link dialog, the two colour panels and the overflow panel are rendered as SIBLINGS of
     * this div, so their own controls stay ordinary tab stops (they exist only while open, and
     * they were opened deliberately).
     *
     * `disabled` controls are skipped — the pattern requires the roving stop to land on
     * something operable. Nothing in the strip is disabled today; the filter is there so that
     * the first control that becomes so does not become a dead stop.
     */
    const controls = useCallback(
      () =>
        toolbarRef.current
          ? Array.from(toolbarRef.current.querySelectorAll("button")).filter(
              (btn) => !btn.disabled,
            )
          : [],
      [],
    );

    /**
     * Move the roving stop onto `el` and put focus there.
     *
     * `scrollIntoView` because `.rte-toolbar` is `overflow: auto` and the strip really does
     * clip: KDS measured the image/video/table tools scrolling out of the visible track at
     * 1024px and 960px (see `styles/kcEdit.scss`'s note on the reserved right-hand gutter).
     * Arrowing onto a control that is outside the track would otherwise focus something the
     * user cannot see. `nearest` on both axes so it never scrolls the page around the editor.
     *
     * THE `tabIndex=0` FOLLOWS FOCUS ONLY WHEN THE STRIP IS A TAB STOP. With
     * `toolbarTabStop` off (the default) the controls stay at -1 and are focused directly —
     * otherwise arrowing around after an Alt+F10 would quietly PUT the strip into the tab
     * order and undo the thing the default is for.
     */
    const focusControl = useCallback(
      (el) => {
        if (toolbarTabStop) {
          controls().forEach((btn) => {
            btn.tabIndex = btn === el ? 0 : -1;
          });
        }
        activeControlRef.current = el;
        el.focus();
        el.scrollIntoView?.({ block: "nearest", inline: "nearest" });
      },
      [controls, toolbarTabStop],
    );

    /**
     * WHERE THE ROVING STOP SITS, RE-ESTABLISHED AFTER EVERY RENDER.
     *
     * Every button's JSX hard-codes `tabIndex={-1}` (unchanged from before this pattern), so
     * React writes that attribute once at mount and never diffs it again — which is what lets
     * this set the 0 imperatively without React fighting it back. A control that unmounts and
     * remounts comes back at -1 and this puts the 0 back on the next commit.
     *
     * The previously-active control keeps the stop if it is still on screen; otherwise it falls
     * back to the first control, so the toolbar is never left with no way in (the case that
     * matters: leaving a table removes the group the stop was sitting in).
     *
     * **THE `tabIndex=0` IS WRITTEN ONLY WHEN `toolbarTabStop`, WHICH IS OFF BY DEFAULT — but
     * `activeControlRef` IS TRACKED EITHER WAY.** Those two halves used to be one: opting out
     * cleared the memory and killed arrow navigation with it, because with nothing focusable by
     * Tab nothing could ever be focused at all. Alt+F10 is now the way in (see
     * `MarkdownEditor`'s wrapper handler, which calls the `focusToolbar` this component
     * exposes), and it focuses a `tabIndex=-1` control directly — perfectly legal, and it is
     * what keeps the strip out of the SEQUENTIAL order while still being reachable. So the ring
     * below is live in both modes; the flag only decides whether Tab can also reach it.
     *
     * No dependency array on purpose: the strip re-renders on every selection change, and the
     * set of controls can change with it. The body is a handful of attribute writes.
     */
    useEffect(() => {
      const items = controls();
      if (!items.length) {
        activeControlRef.current = null;
        return;
      }
      const keep = items.includes(activeControlRef.current)
        ? activeControlRef.current
        : items[0];
      activeControlRef.current = keep;
      items.forEach((btn) => {
        btn.tabIndex = toolbarTabStop && btn === keep ? 0 : -1;
      });
    });

    /**
     * THE KEYBOARD ENTRY POINT THAT COSTS NO TAB STOP — `Alt+F10`, the keystroke CKEditor and
     * TinyMCE both use for "focus the editor toolbar".
     *
     * Exposed as the component's imperative handle rather than bound here, because the key is
     * pressed while the PROSE has focus: the wrapper is the only element that sees both. See
     * `MarkdownEditor`'s `handleEditorKeyDown`.
     *
     * It lands on the control the author last used (`activeControlRef`), first control
     * otherwise — the same place a Tab would land when `toolbarTabStop` is on, so the two
     * routes in are not two different states.
     */
    const focusToolbar = useCallback(() => {
      const items = controls();
      if (!items.length) return false;
      const target = items.includes(activeControlRef.current)
        ? activeControlRef.current
        : items[0];
      focusControl(target);
      return true;
    }, [controls, focusControl]);

    useImperativeHandle(ref, () => ({ focusToolbar }), [focusToolbar]);

    /**
     * THE ARIA TOOLBAR KEYBOARD (added 2026-08-27).
     *
     * Left/Right move between the controls and wrap; Home/End jump to the ends. The strip is
     * ONE stop's worth of keyboard however many tools it holds — Tab leaves it rather than
     * walking it (and by default Tab cannot reach it at all; Alt+F10 is the way in).
     *
     * ---- ESCAPE IS THE WAY BACK OUT, AND IT IS ONE ACTION PER PRESS ----
     *
     * With no panel open, Escape at the toolbar returns focus to the prose (`onEscapeToProse`)
     * and goes no further: `stopPropagation` so it never reaches the embedding form, whose own
     * Escape is "cancel this" (KDS's section card). Leaving the toolbar must not discard the
     * author's work.
     *
     * With a PANEL open it is deliberately NOT handled here — the wrapper's own Escape closes
     * the panel and returns focus to the trigger, which is a control in this strip. So a
     * palette opened from the keyboard takes two presses: one to close the palette, one to
     * leave the strip. That ordering is the whole reason this branch tests the four flags
     * rather than assuming focus implies "nothing open": Enter on a colour button opens the
     * panel WITHOUT moving focus off the button (the re-dispatched `mousedown` preventDefaults
     * itself), so both conditions really do co-occur.
     *
     * `defaultPrevented` is honoured for the same reason the wrapper honours it — the link
     * dialog's input handles its own Escape — and it is the contract KDS's card relies on.
     *
     * ---- ENTER AND SPACE, AND WHY THEY DISPATCH A `mousedown` ----
     *
     * Every control in this strip acts on `onMouseDown` and `preventDefault()`s it. That is
     * deliberate and predates this: a `mousedown` that never completes its default action
     * leaves the editor's selection exactly where it was, which is the whole reason clicking
     * "Bold" bolds the words you had selected instead of the empty caret the click would
     * otherwise have moved. The cost was that the buttons had NO keyboard activation at all —
     * Enter and Space fire `click`, which nothing here listens for — so making the strip
     * reachable by keyboard without this would have reached a row of inert buttons.
     *
     * Re-dispatching the same `mousedown` the mouse would have sent runs each control's
     * existing handler unchanged, rather than duplicating twenty-eight actions into a second
     * code path that could drift from the first. `preventDefault()` on the key stops the
     * browser's own click (so nothing fires twice) and stops Space scrolling the page.
     *
     * Note what follows from re-using the mouse path: a formatting command ends with
     * `editor.chain().focus()`, so activating one from the keyboard puts the caret back in the
     * prose — the same place a mouse click leaves it. Shift+Tab returns to the strip.
     *
     * Modified keys are left alone (Alt+Arrow is a section reorder in KDS, Ctrl/Cmd combinations
     * are the app's and the editor's own shortcuts) — the toolbar only claims the plain keys.
     */
    const handleToolbarKeyDown = (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      const target = event.target.closest?.("button");

      if (event.key === "Escape") {
        if (event.defaultPrevented) return;
        // A panel is open: the wrapper's Escape owns this press (see the note above).
        if (linkOpen || overflowOpen || colorOpen || highlightOpen) return;
        // The key has to have come from a control in this strip, not from the prose — the
        // wrapper renders both and this handler is on the strip, but a panel is a sibling.
        if (!target || !onEscapeToProse) return;
        event.preventDefault();
        event.stopPropagation();
        onEscapeToProse();
        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        if (!target || target.disabled) return;
        event.preventDefault();
        event.stopPropagation();
        target.dispatchEvent(
          new MouseEvent("mousedown", { bubbles: true, cancelable: true }),
        );
        return;
      }

      const isArrow = event.key === "ArrowRight" || event.key === "ArrowLeft";
      if (!isArrow && event.key !== "Home" && event.key !== "End") return;

      const items = controls();
      if (!items.length) return;

      const from = items.indexOf(target);
      let next;
      if (event.key === "Home") next = items[0];
      else if (event.key === "End") next = items[items.length - 1];
      else if (from === -1) next = items[0];
      else {
        const step = event.key === "ArrowRight" ? 1 : -1;
        next = items[(from + step + items.length) % items.length];
      }

      event.preventDefault();
      event.stopPropagation();
      focusControl(next);
    };

    // Helper functions
    const handleColorSelect = (color) => {
      if (color === null) {
        editor.chain().focus().unsetColor().run();
      } else {
        editor.chain().focus().setColor(color).run();
      }
      setColorOpen(false);
    };

    const handleHighlightSelect = (color) => {
      if (color === null) {
        editor.chain().focus().unsetBackgroundColor().run();
      } else {
        editor.chain().focus().setBackgroundColor(color).run();
      }
      setHighlightOpen(false);
    };

    const handleLinkOpen = (e) => {
      e.preventDefault();
      if (!linkOpen) {
        const rect = linkBtnRef.current?.getBoundingClientRect();
        if (rect) setLinkPos({ top: rect.bottom + 4, left: rect.left });
        setLinkValue(editor?.getAttributes("link").href || "");
      }
      setLinkOpen((o) => !o);
    };

    const handleLinkApply = (url) => {
      const trimmed = url.trim();
      if (!trimmed) {
        editor.chain().focus().unsetLink().run();
        setLinkOpen(false);
        return;
      }

      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;

      if (!hasSelection && !editor.isActive("link")) {
        // No text selected: insert the URL itself as linked text
        editor
          .chain()
          .focus()
          .insertContent([
            {
              type: "text",
              text: trimmed,
              marks: [{ type: "link", attrs: { href: trimmed } }],
            },
          ])
          .run();
      } else {
        editor.chain().focus().setLink({ href: trimmed }).run();
      }
      setLinkOpen(false);
    };

    const handleLinkRemove = () => {
      editor.chain().focus().unsetLink().run();
      setLinkOpen(false);
    };

    const handleTableInsert = () => {
      editor
        .chain()
        .focus()
        .insertTable({ rows: 3, cols: 2, withHeaderRow: true })
        .run();
      // insertTable leaves every cell's `colwidth` null, which pins the table at the stylesheet's
      // 100% and makes its width undraggable (see seedTableColumnWidths). Seeding right after insert
      // — once the new table has been laid out, hence the rAF — means a table is resizable as a whole
      // from the moment it exists, at exactly the size it already renders.
      requestAnimationFrame(() => seedTableColumnWidths(editor));
    };

    // Visibility flags
    const showHeadings = hasTool("headings") && !isOverflow("headings");
    const showBold = hasTool("bold");
    const showItalic = hasTool("italic");
    const showLink = hasTool("link");
    const showColor = hasTool("color");
    const showHighlight = hasTool("highlight");
    const showAlign = hasTool("align") && !isOverflow("align");
    const showFormatting =
      showBold ||
      showItalic ||
      showLink ||
      showColor ||
      showHighlight ||
      showAlign;
    const showLists = hasTool("lists") && !isOverflow("lists");
    const showTableInsert = hasTool("table") && !isOverflow("table");
    const showTableContextual = hasTool("table") && isInTable;
    const showImage =
      hasTool("image") && !!onUploadImage && !isOverflow("image");
    const showVideo =
      hasTool("video") && !!onUploadVideo && !isOverflow("video");
    const showMedia =
      showImage || showVideo || showTableInsert || showTableContextual;
    const hasAnyOverflow = [
      "headings",
      "align",
      "lists",
      "image",
      "video",
      "table",
      "strike",
      "blockquote",
      "code",
      "codeblock",
      "horizontalrule",
    ].some((t) => hasTool(t) && isOverflow(t));
    const showOverflow = hasAnyOverflow;
    const showSecondary = [
      "strike",
      "blockquote",
      "code",
      "codeblock",
      "horizontalrule",
    ].some((t) => hasTool(t) && !isOverflow(t));

    const sectionFlags = [
      showHeadings,
      showFormatting,
      showLists,
      showMedia,
      showSecondary,
      showOverflow,
    ];
    const hasAfter = (i) => sectionFlags.slice(i + 1).some(Boolean);

    // ---- EVERY BUTTON STILL CARRIES `tabIndex={-1}` IN THE JSX, FOR A NEW REASON ----
    //
    // It used to be the whole mechanism: the strip is rendered ABOVE the typing area, so with
    // every icon a tab stop, Tab out of a preceding field (a form's Title input, say) walked the
    // user through two dozen of them before the caret reached the text. A blanket -1 fixed that
    // by taking the toolbar OUT of the tab order entirely, leaving the editable area as the
    // editor's single stop — and the comment here argued that nothing became unreachable because
    // the formatting actions have keyboard shortcuts. That was not true: colour, highlight,
    // alignment, the table tools and image/video have no shortcut, and the buttons act on
    // `mousedown`, so even a focused one could not be activated. A pointer was required.
    //
    // Since 2026-08-27 the -1 is instead the RESTING VALUE of a roving tabindex (see the
    // roving-tabindex effect and `handleToolbarKeyDown` above): Left/Right/Home/End move
    // between the controls, Enter/Space activate one, and Escape returns to the prose. This is
    // the standard ARIA toolbar pattern. The reason the -1 stays hard-coded in the JSX is
    // mechanical — React writes the attribute once and never diffs a constant, which is what
    // lets the effect own the 0 without being overwritten on the next render.
    //
    // **THE STRIP IS NOT IN THE SEQUENTIAL TAB ORDER, AND THAT IS THE DEFAULT
    // (`toolbarTabStop = false`).** It was briefly the other way round, and that was wrong:
    // two consumers have "the field above, then Tab, then the prose" as a WRITTEN requirement
    // rather than a preference —
    //
    //   - KDS's section card: change note 4e, "type the title, tab to the body, tab to save"
    //     (`components/kc-edit/KcSectionCard`'s class comment, pinned in `pages/KcEdit.test`);
    //   - PDS's change request 10geo, "title -> Tab -> description" across about two dozen
    //     forms — `dos-frontend`'s `services/utils.js` `focusDescriptionEditor()` says in its
    //     own comment that it comes for free BECAUSE the toolbar is out of the tab order.
    //
    // Since both were already true when every button carried a blanket -1, putting the strip
    // into the order broke them for a benefit that Alt+F10 buys instead: pressed in the prose
    // it focuses the roving control, and Escape comes back. Zero tab stops AND reachable, which
    // is what the blanket -1 could never be.
    //
    // `toolbarTabStop={true}` is kept as an opt-in for a form that would rather have the
    // old-fashioned stop. It changes exactly one thing — whether one control carries the 0 —
    // and nothing about the arrow ring or Alt+F10, which work in both modes.
    //
    // The floating panels (link, colour, overflow) are exempt from all of this — they are
    // rendered as siblings of this div, exist only while open, and are opened deliberately, so
    // their own controls stay ordinary tab stops.
    return (
      <>
        <div
          ref={setToolbarRef}
          className="rte-toolbar"
          role="toolbar"
          aria-label="Formatting"
          /* A KEYSTROKE NOBODY CAN DISCOVER IS NOT ACCESS. Alt+F10 is the only way into a
             strip that is (by default) not in the tab order, so it is declared where an
             assistive technology can read it out rather than left to the docs. `aria-
             keyshortcuts` is exactly this: "the shortcut that gives focus to this element".
             The same attribute is put on the PROSE by `MarkdownEditor`, because that is where
             the author is standing when they need to hear it. */
          aria-keyshortcuts="Alt+F10"
          aria-orientation="horizontal"
          onKeyDown={handleToolbarKeyDown}
        >
          {/* Headings */}
          {showHeadings && (
            <>
              <button
                type="button"
                tabIndex={-1}
                title="Heading 1"
                className={`rte-btn${
                  editor?.isActive("heading", { level: 1 }) ? " active" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleHeading({ level: 1 }).run();
                }}
              >
                Title
              </button>
              <button
                type="button"
                tabIndex={-1}
                title="Heading 2"
                className={`rte-btn${
                  editor?.isActive("heading", { level: 2 }) ? " active" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleHeading({ level: 2 }).run();
                }}
              >
                Subtitle
              </button>
              <button
                type="button"
                tabIndex={-1}
                title="Heading 3"
                className={`rte-btn${
                  editor?.isActive("heading", { level: 3 }) ? " active" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleHeading({ level: 3 }).run();
                }}
              >
                Normal
              </button>
              {hasAfter(0) && <ToolbarDivider />}
            </>
          )}

          {/* Formatting: Bold, Italic, Color, Highlight, Align */}
          {showFormatting && (
            <>
              {showBold && (
                <button
                  type="button"
                  tabIndex={-1}
                  title="Bold"
                  className={`rte-btn${editor?.isActive("bold") ? " active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleBold().run();
                  }}
                >
                  <span className="material-symbols-outlined">format_bold</span>
                </button>
              )}
              {showItalic && (
                <button
                  type="button"
                  tabIndex={-1}
                  title="Italic"
                  className={`rte-btn${editor?.isActive("italic") ? " active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleItalic().run();
                  }}
                >
                  <span className="material-symbols-outlined">
                    format_italic
                  </span>
                </button>
              )}
              {showLink && (
                <button
                  ref={linkBtnRef}
                  type="button"
                  tabIndex={-1}
                  title="Insert link"
                  className={`rte-btn${editor?.isActive("link") || linkOpen ? " active" : ""}`}
                  onMouseDown={handleLinkOpen}
                >
                  <span className="material-icons">link</span>
                </button>
              )}
              {showColor && (
                <button
                  ref={colorBtnRef}
                  type="button"
                  tabIndex={-1}
                  title="Text color"
                  className={`rte-btn${colorOpen ? " active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!colorOpen) {
                      const rect = colorBtnRef.current?.getBoundingClientRect();
                      if (rect)
                        setColorPos({ top: rect.bottom + 4, left: rect.left });
                    }
                    setColorOpen((o) => !o);
                  }}
                >
                  <span className="rte-color-btn-icon">
                    <span className="material-symbols-outlined">colors</span>
                    <span
                      className="rte-color-indicator"
                      style={{ background: activeColor || "#000000" }}
                    />
                  </span>
                </button>
              )}
              {showHighlight && (
                <button
                  ref={highlightBtnRef}
                  type="button"
                  tabIndex={-1}
                  title="Highlight color"
                  className={`rte-btn${highlightOpen ? " active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!highlightOpen) {
                      const rect =
                        highlightBtnRef.current?.getBoundingClientRect();
                      if (rect)
                        setHighlightPos({
                          top: rect.bottom + 4,
                          left: rect.left,
                        });
                    }
                    setHighlightOpen((o) => !o);
                  }}
                >
                  <span className="rte-color-btn-icon">
                    <span className="material-symbols-outlined">
                      ink_highlighter
                    </span>
                    <span
                      className="rte-color-indicator"
                      style={{ background: activeHighlight || "#FEF08A" }}
                    />
                  </span>
                </button>
              )}
              {showAlign && (
                <>
                  <button
                    type="button"
                    tabIndex={-1}
                    title="Align left"
                    className={`rte-btn${
                      activeAlign === "left" || !activeAlign ? " active" : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.chain().focus().setTextAlign("left").run();
                    }}
                  >
                    <span className="material-icons">format_align_left</span>
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    title="Align center"
                    className={`rte-btn${
                      activeAlign === "center" ? " active" : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.chain().focus().setTextAlign("center").run();
                    }}
                  >
                    <span className="material-icons">format_align_center</span>
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    title="Align right"
                    className={`rte-btn${
                      activeAlign === "right" ? " active" : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.chain().focus().setTextAlign("right").run();
                    }}
                  >
                    <span className="material-icons">format_align_right</span>
                  </button>
                </>
              )}
              {hasAfter(1) && <ToolbarDivider />}
            </>
          )}

          {/* Lists */}
          {showLists && (
            <>
              <button
                type="button"
                tabIndex={-1}
                title="Bullet list"
                className={`rte-btn${
                  editor?.isActive("bulletList") ? " active" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleBulletList().run();
                }}
              >
                <span className="material-icons">format_list_bulleted</span>
              </button>
              <button
                type="button"
                tabIndex={-1}
                title="Ordered list"
                className={`rte-btn${
                  editor?.isActive("orderedList") ? " active" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleOrderedList().run();
                }}
              >
                <span className="material-icons">format_list_numbered</span>
              </button>
              {hasAfter(2) && <ToolbarDivider />}
            </>
          )}

          {/* Media: Image + Table insert button */}
          {showMedia && (
            <>
              {showImage && (
                <button
                  type="button"
                  tabIndex={-1}
                  title="Insert image"
                  className="rte-btn"
                  onClick={async (e) => {
                    e.preventDefault();
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.multiple = false;
                    input.onchange = async (ev) => {
                      const file = ev.target.files[0];
                      if (file) await handleImageFiles([file]);
                    };
                    input.click();
                  }}
                >
                  <span className="material-icons">image</span>
                </button>
              )}
              {showVideo && (
                <button
                  type="button"
                  tabIndex={-1}
                  title="Insert video"
                  className="rte-btn"
                  onClick={async (e) => {
                    e.preventDefault();
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "video/*";
                    input.multiple = false;
                    input.onchange = async (ev) => {
                      const file = ev.target.files[0];
                      if (file) await handleVideoFiles([file]);
                    };
                    input.click();
                  }}
                >
                  <span className="material-icons">videocam</span>
                </button>
              )}
              {showTableInsert && (
                <button
                  type="button"
                  tabIndex={-1}
                  title="Insert table"
                  className="rte-btn"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleTableInsert();
                  }}
                >
                  <span className="material-icons">table_chart</span>
                </button>
              )}
              {showTableContextual && (
                <>
                  <ToolbarDivider />
                  <button
                    type="button"
                    tabIndex={-1}
                    title="Add row above"
                    className="rte-btn"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.chain().focus().addRowBefore().run();
                    }}
                  >
                    <span className="material-symbols-outlined">
                      add_row_above
                    </span>
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    title="Add row below"
                    className="rte-btn"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.chain().focus().addRowAfter().run();
                    }}
                  >
                    <span className="material-symbols-outlined">
                      add_row_below
                    </span>
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    title="Delete row"
                    className="rte-btn"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.chain().focus().deleteRow().run();
                    }}
                  >
                    <span className="material-symbols-outlined">
                      variable_remove
                    </span>
                  </button>
                  <ToolbarDivider />
                  <button
                    type="button"
                    tabIndex={-1}
                    title="Add column before"
                    className="rte-btn"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.chain().focus().addColumnBefore().run();
                    }}
                  >
                    <span className="material-symbols-outlined">
                      add_column_left
                    </span>
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    title="Add column after"
                    className="rte-btn"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.chain().focus().addColumnAfter().run();
                    }}
                  >
                    <span className="material-symbols-outlined">
                      add_column_right
                    </span>
                  </button>
                  <button
                    type="button"
                    tabIndex={-1}
                    title="Delete column"
                    className="rte-btn"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.chain().focus().deleteColumn().run();
                    }}
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ transform: "scaleX(-1) rotate(90deg)" }}
                    >
                      variable_remove
                    </span>
                  </button>
                </>
              )}
              {hasAfter(3) && <ToolbarDivider />}
            </>
          )}

          {/* Secondary tools — shown directly in toolbar when not sent to overflow */}
          {showSecondary && (
            <>
              {hasTool("strike") && !isOverflow("strike") && (
                <button
                  type="button"
                  tabIndex={-1}
                  title="Strikethrough"
                  className={`rte-btn${editor?.isActive("strike") ? " active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleStrike().run();
                  }}
                >
                  <s>S</s>
                </button>
              )}
              {hasTool("blockquote") && !isOverflow("blockquote") && (
                <button
                  type="button"
                  tabIndex={-1}
                  title="Blockquote"
                  className={`rte-btn${
                    editor?.isActive("blockquote") ? " active" : ""
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleBlockquote().run();
                  }}
                >
                  <span className="material-icons">format_quote</span>
                </button>
              )}
              {hasTool("code") && !isOverflow("code") && (
                <button
                  type="button"
                  tabIndex={-1}
                  title="Inline code"
                  className={`rte-btn${editor?.isActive("code") ? " active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleCode().run();
                  }}
                >
                  <span className="material-icons">code</span>
                </button>
              )}
              {hasTool("codeblock") && !isOverflow("codeblock") && (
                <button
                  type="button"
                  tabIndex={-1}
                  title="Code block"
                  className={`rte-btn${
                    editor?.isActive("codeBlock") ? " active" : ""
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().toggleCodeBlock().run();
                  }}
                >
                  <span className="material-icons">data_object</span>
                </button>
              )}
              {hasTool("horizontalrule") && !isOverflow("horizontalrule") && (
                <button
                  type="button"
                  tabIndex={-1}
                  title="Horizontal rule"
                  className="rte-btn"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    editor.chain().focus().setHorizontalRule().run();
                  }}
                >
                  <span className="material-icons">horizontal_rule</span>
                </button>
              )}
            </>
          )}

          {/* Overflow trigger button */}
          {showOverflow && (
            <button
              ref={overflowBtnRef}
              type="button"
              tabIndex={-1}
              title="More formatting"
              className={`rte-btn${overflowOpen ? " active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault();
                if (!overflowOpen) {
                  const rect = overflowBtnRef.current?.getBoundingClientRect();
                  if (rect)
                    setOverflowPos({ top: rect.bottom + 4, left: rect.left });
                }
                setOverflowOpen((o) => !o);
              }}
            >
              <span className="material-icons">more_horiz</span>
            </button>
          )}
        </div>

        {/* Link dialog */}
        <LinkDialog
          ref={linkDialogRef}
          pos={linkOpen ? linkPos : null}
          value={linkValue}
          onChange={setLinkValue}
          onApply={handleLinkApply}
          onRemove={handleLinkRemove}
          isActive={editor?.isActive("link") ?? false}
        />

        {/* Color picker panel */}
        <ColorPanel
          ref={colorPanelRef}
          colors={COLOR_PRESETS}
          pos={colorOpen ? colorPos : null}
          onColorSelect={handleColorSelect}
          activeColor={activeColor}
        />

        {/* Highlight picker panel */}
        <ColorPanel
          ref={highlightPanelRef}
          colors={HIGHLIGHT_PRESETS}
          pos={highlightOpen ? highlightPos : null}
          onColorSelect={handleHighlightSelect}
          activeColor={activeHighlight}
        />

        {/* Overflow panel */}
        <OverflowPanel
          ref={overflowRef}
          pos={overflowOpen ? overflowPos : null}
          hasTool={hasTool}
          isOverflow={isOverflow}
          editor={editor}
          onUploadImage={onUploadImage}
          handleImageFiles={handleImageFiles}
          onUploadVideo={onUploadVideo}
          handleVideoFiles={handleVideoFiles}
          setOverflowOpen={setOverflowOpen}
          onTableInsert={handleTableInsert}
        />
      </>
    );
  },
);

RichTextToolbar.displayName = "RichTextToolbar";

export default RichTextToolbar;
